/* global __webpack_require__ */
import React, { Profiler } from "react";
import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Skeleton } from "@esotericsoftware/spine-core";
import { FEATURES_CONFIG } from "../featuresConfig";

/**
 * resizeProfiler — a DEV tool (FEATURES_CONFIG.DEV_TOOLS.RESIZE_PROFILER).
 *
 * Times everything that runs because of a window resize and prints one ranked
 * table to the console once resizing has been quiet for QUIET_MS:
 *
 *   - frame gaps (ticker to ticker) and browser long tasks, to size the spike;
 *   - React render time per subtree (<ResizeProbe id="..."> in Stage);
 *   - PIXI: text re-rasterising, renderer.render (draw + GPU uploads),
 *     renderer.resize (canvas);
 *   - Spine: renderMeshes, setSkin, setSlotsToSetupPose;
 *   - every loaded class whose method names look like layout work (resize,
 *     dimension, scale, skin, orientation, text, warm, ...), found through
 *     webpack's module cache and labelled `Class.method`.
 *
 * Times are inclusive: a method's time includes what it calls. Outside a
 * resize session every wrapper is one boolean check.
 */

const ENABLED = !!FEATURES_CONFIG.DEV_TOOLS?.RESIZE_PROFILER;
const QUIET_MS = 1000;
const TOP_ROWS = 40;
const METHOD_PATTERN =
  /resize|dimension|scale|layout|skin|orientation|text|warm|density|fit|center|position|config/i;
const CLASS_PATTERN = /(Controller|Extractor|Animator|Manager|Generator)$/;

const session = {
  active: false,
  start: 0,
  last: 0,
  events: 0,
  frameAt: 0,
  frames: [],
  longTasks: [],
  buckets: new Map(),
  // This frame's TOP-LEVEL measured time (nested calls are not added twice)
  // and its biggest items; frames over SLOW_FRAME_MS are kept for the report.
  frameMs: 0,
  frameItems: new Map(),
  slowFrames: [],
};

const SLOW_FRAME_MS = 50;
let depth = 0;

let installed = false;
const now = () => performance.now();

const add = (label, ms, topLevel = true) => {
  if (topLevel) {
    session.frameMs += ms;
    session.frameItems.set(label, (session.frameItems.get(label) || 0) + ms);
  }
  const bucket = session.buckets.get(label);
  if (bucket) {
    bucket.ms += ms;
    bucket.calls += 1;
  } else {
    session.buckets.set(label, { ms, calls: 1 });
  }
};

/**
 * Wrap `owner[name]` with a timer. `when` decides per call whether to time it;
 * `label` may be a function of the instance.
 */
const wrap = (owner, name, label, when = null) => {
  const original = owner?.[name];
  if (typeof original !== "function" || original.__resizeProfiled) return;
  const timed = function (...args) {
    if (!session.active || (when && !when(this, args))) {
      return original.apply(this, args);
    }
    const t = now();
    depth += 1;
    try {
      return original.apply(this, args);
    } finally {
      depth -= 1;
      add(typeof label === "function" ? label(this) : label, now() - t, depth === 0);
    }
  };
  timed.__resizeProfiled = true;
  try {
    owner[name] = timed;
  } catch {
    // read-only property — skip it
  }
};

const wrapLibraries = () => {
  wrap(
    PIXI.Text.prototype,
    "updateText",
    (text) =>
      text.worldAlpha > 0.01
        ? "PIXI Text re-raster (visible)"
        : "PIXI Text re-raster (hidden)",
    (text, [respectDirty]) => !(respectDirty && !text.dirty),
  );
  wrap(PIXI.BitmapText.prototype, "updateText", "PIXI BitmapText layout");
  wrap(PIXI.Renderer.prototype, "render", "PIXI renderer.render (draw + GPU uploads)");
  wrap(PIXI.Renderer.prototype, "resize", "PIXI renderer.resize (canvas)");
  wrap(Spine.prototype, "renderMeshes", "Spine renderMeshes (all spines)");
  wrap(Skeleton.prototype, "setSkin", "Spine setSkin");
  wrap(Skeleton.prototype, "setSlotsToSetupPose", "Spine setSlotsToSetupPose");
};

const wrapClass = (Ctor) => {
  const className = Ctor.name;
  if (!className || !CLASS_PATTERN.test(className)) return 0;
  let count = 0;
  const targets = [
    [Ctor.prototype, `${className}.`],
    [Ctor, `${className}.static `],
  ];
  for (const [owner, prefix] of targets) {
    for (const key of Object.getOwnPropertyNames(owner)) {
      if (key === "constructor" || !METHOD_PATTERN.test(key)) continue;
      const descriptor = Object.getOwnPropertyDescriptor(owner, key);
      if (!descriptor || typeof descriptor.value !== "function") continue;
      wrap(owner, key, `${prefix}${key}`);
      count += 1;
    }
  }
  return count;
};

/** Every class in a loaded module of ours, found through webpack's cache. */
const wrapLoadedClasses = () => {
  const cache =
    typeof __webpack_require__ !== "undefined" ? __webpack_require__.c : null;
  if (!cache) {
    console.warn("[resize-profile] webpack module cache unavailable — classes not timed");
    return 0;
  }
  let methods = 0;
  const seen = new Set();
  for (const [id, mod] of Object.entries(cache)) {
    if (String(id).includes("node_modules")) continue;
    const exports = mod?.exports;
    if (!exports || typeof exports !== "object") continue;
    for (const key of Object.keys(exports)) {
      let value;
      try {
        value = exports[key];
      } catch {
        continue;
      }
      if (typeof value !== "function" || !value.prototype || seen.has(value)) continue;
      seen.add(value);
      methods += wrapClass(value);
    }
  }
  return methods;
};

const report = () => {
  session.active = false;
  const frames = session.frames;
  const longest = frames.length ? Math.max(...frames) : 0;
  const slow = frames.filter((f) => f > 50).map((f) => Math.round(f));
  const rows = [...session.buckets]
    .map(([what, { ms, calls }]) => ({ ms: Math.round(ms * 10) / 10, calls, what }))
    .sort((a, b) => b.ms - a.ms)
    .slice(0, TOP_ROWS);

  const header =
    `${session.events} resize events over ${Math.round(session.last - session.start)}ms · ` +
    `longest frame ${Math.round(longest)}ms · frames >50ms: ${slow.join(", ") || "none"} · ` +
    `long tasks: ${session.longTasks.join(", ") || "none"}`;
  const frameLines = session.slowFrames.map((f) => {
    const items = [...f.items]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([what, ms]) => `${what} ${Math.round(ms)}`)
      .join(", ");
    return (
      `  frame ${Math.round(f.gap)}ms = game code ${Math.round(f.measured)}ms` +
      ` [${items || "nothing measured"}] + not ours ${Math.round(f.gap - f.measured)}ms` +
      ` (browser layout, canvas/GPU, DevTools)`
    );
  });
  const text = rows
    .map((r) => `${String(r.ms).padStart(8)}ms ${String(r.calls).padStart(5)}x  ${r.what}`)
    .join("\n");
  console.log(
    `%c[resize-profile]%c ${header}\n` +
      `SLOW FRAMES:\n${frameLines.join("\n") || "  none"}\n` +
      `WHOLE SESSION (inclusive — a method includes what it calls):\n${text}`,
    "color:#e67e22;font-weight:bold",
    "color:inherit",
  );
};

const onTick = () => {
  if (!session.active) return;
  const t = now();
  const gap = t - session.frameAt;
  session.frames.push(gap);
  if (gap > SLOW_FRAME_MS) {
    session.slowFrames.push({
      gap,
      measured: session.frameMs,
      items: new Map(session.frameItems),
    });
  }
  session.frameMs = 0;
  session.frameItems.clear();
  session.frameAt = t;
  if (t - session.last > QUIET_MS) report();
};

/** Call at the top of the window resize handler. */
export const markResize = () => {
  if (!ENABLED || !installed) return;
  const t = now();
  if (!session.active) {
    session.active = true;
    session.start = t;
    session.events = 0;
    session.frameAt = t;
    session.frames.length = 0;
    session.longTasks.length = 0;
    session.buckets.clear();
    session.slowFrames.length = 0;
    session.frameMs = 0;
    session.frameItems.clear();
  }
  session.last = t;
  session.events += 1;
};

/** Install once, after the game's modules have loaded. */
export const installResizeProfiler = () => {
  if (!ENABLED || installed) return;
  installed = true;
  // DimensionsContext's resize handler calls this (no import there).
  globalThis.__markResize = markResize;
  wrapLibraries();
  const methods = wrapLoadedClasses();
  PIXI.Ticker.shared.add(onTick, null, PIXI.UPDATE_PRIORITY.INTERACTION);
  try {
    new PerformanceObserver((list) => {
      if (!session.active) return;
      for (const entry of list.getEntries()) {
        session.longTasks.push(Math.round(entry.duration));
      }
    }).observe({ entryTypes: ["longtask"] });
  } catch {
    // long tasks not supported in this browser
  }
  console.log(
    `[resize-profile] ON — timing ${methods} class methods. Resize the window, wait ~1s, read the table.`,
  );
};

const onRender = (id, _phase, actualDuration) => {
  if (session.active) add(`React render · ${id}`, actualDuration, depth === 0);
};

// A probe inside another probe: listed, but not added to the frame total
// again (the outer probe's time already includes it).
const onNestedRender = (id, _phase, actualDuration) => {
  if (session.active) add(`React render · ${id}`, actualDuration, false);
};

/** Wraps a subtree in a React Profiler while the tool is on. */
export const ResizeProbe = ({ id, nested = false, children }) =>
  ENABLED
    ? React.createElement(
        Profiler,
        { id, onRender: nested ? onNestedRender : onRender },
        children,
      )
    : children;
